import { Button, Checkbox, Field, Input, Link as CLink, Stack, Text } from '@chakra-ui/react'
import { EmailSchema } from '@ipc/shared'
import { useMutation } from '@tanstack/react-query'
import { useId, useRef, useState, type FormEvent } from 'react'
import { useNavigate, useOutletContext } from 'react-router'
import { api } from '~/api/client'
import { ActionBar } from '~/components/member/ActionBar'
import { FlowStepper } from '~/components/member/FlowStepper'
import { ErrorAlert, useFriendlyError } from '~/components/member/feedback'
import { FormStack, MemberLayout } from '~/components/member/layout'
import { Rich, useT } from '~/i18n/use-t'
import { readDraft, saveDraft } from '~/lib/draft'
import { pathForNext } from '~/lib/flow'
import type { JoinContext } from './JoinRoute'

const FORM_ID = 'about-form'

interface Errors {
  name?: string
  email?: string
  consent?: string
}

/** S1 About you (Step n of N). It never says an email is refused: every valid-looking email goes on to S2 (U3, R11). */
export default function AboutYou() {
  const { t } = useT()
  const friendly = useFriendlyError()
  const navigate = useNavigate()
  const { join } = useOutletContext<JoinContext>()
  const providerId = join.providerId
  const draft = readDraft()
  const [name, setName] = useState(draft.name ?? '')
  const [email, setEmail] = useState(draft.email ?? '')
  const [consent, setConsent] = useState(false)
  const [errors, setErrors] = useState<Errors>({})
  const nameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const consentRef = useRef<HTMLInputElement>(null)
  const consentErrId = useId()

  const start = useMutation({
    mutationFn: () =>
      api('registrationStart', {
        body: { provider: providerId, name: name.trim(), email: email.trim(), acceptPrivacy: true },
      }),
    onSuccess: (res) => {
      saveDraft({ name: name.trim(), email: email.trim(), resendAt: Date.now() + res.resendAfterSeconds * 1000 })
      navigate(pathForNext(providerId, res.next))
    },
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const next: Errors = {}
    if (!name.trim()) next.name = t('about.errName')
    if (!EmailSchema.safeParse(email.trim()).success) next.email = t('about.errEmail')
    if (!consent) next.consent = t('about.errConsent')
    setErrors(next)
    if (next.name) nameRef.current?.focus()
    else if (next.email) emailRef.current?.focus()
    else if (next.consent) consentRef.current?.focus()
    if (Object.keys(next).length === 0) start.mutate()
  }

  return (
    <MemberLayout backTo={`/join/${providerId}`}>
      <FlowStepper step='about' />
      <FormStack id={FORM_ID} gap={5} onSubmit={submit} noValidate>
        <Field.Root invalid={Boolean(errors.name)}>
          <Field.Label>{t('about.name')}</Field.Label>
          <Input
            ref={nameRef}
            value={name}
            autoComplete='name'
            autoCapitalize='words'
            enterKeyHint='next'
            maxLength={100}
            onChange={(e) => {
              setName(e.target.value)
              saveDraft({ name: e.target.value })
              setErrors((x) => ({ ...x, name: undefined }))
            }}
          />
          <Field.ErrorText>{errors.name}</Field.ErrorText>
        </Field.Root>
        <Field.Root invalid={Boolean(errors.email)}>
          <Field.Label>{t('about.email')}</Field.Label>
          <Input
            ref={emailRef}
            type='email'
            value={email}
            autoComplete='email'
            inputMode='email'
            autoCapitalize='none'
            spellCheck={false}
            enterKeyHint='next'
            onChange={(e) => {
              setEmail(e.target.value)
              saveDraft({ email: e.target.value })
              setErrors((x) => ({ ...x, email: undefined }))
            }}
          />
          <Field.HelperText>{t('about.emailHint')}</Field.HelperText>
          <Field.ErrorText>{errors.email}</Field.ErrorText>
        </Field.Root>
        <Stack gap={1}>
          <Checkbox.Root
            checked={consent}
            invalid={Boolean(errors.consent) && !consent}
            onCheckedChange={(e) => {
              setConsent(e.checked === true)
              if (e.checked === true) setErrors((x) => ({ ...x, consent: undefined }))
            }}
            alignItems='flex-start'
          >
            <Checkbox.HiddenInput ref={consentRef} aria-describedby={errors.consent ? consentErrId : undefined} />
            <Checkbox.Control boxSize='5' mt='0.5' />
            <Checkbox.Label>
              <Rich
                k='about.consent'
                components={{
                  // Inline (not Chakra's inline-flex): the link breaks with the sentence instead of leaving "I have
                  // read" alone on a line. The whole label is the 48 px target.
                  a: (
                    <CLink
                      href='/privacy'
                      target='_blank'
                      rel='noopener noreferrer'
                      textDecoration='underline'
                      display='inline'
                    />
                  ),
                }}
              />
            </Checkbox.Label>
          </Checkbox.Root>
          {errors.consent && (
            <Text id={consentErrId} color='fg.error' role='alert'>
              {errors.consent}
            </Text>
          )}
        </Stack>
        {start.isError && <ErrorAlert>{friendly(start.error)}</ErrorAlert>}
        <ActionBar>
          <Button type='submit' form={FORM_ID} size='lg' w='full' loading={start.isPending}>
            {t('about.continue')}
          </Button>
        </ActionBar>
      </FormStack>
    </MemberLayout>
  )
}
